import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BrowserCheckService } from '../../core/services/browser-check.service';
import { AdminManageService } from '../../core/services/adminService/admin-manage.service';
import { PermissionCacheService } from '../../core/services/permission-cache.service';
import { constantTxt } from '../../core/constants/constants';
import { CalService } from '../../auth/cal.service';
import { UserData } from '../../core/interfaces/user';
import { SessionStorageService } from '../../core/services/session-storage';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, RouterModule],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.less'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  isUserDropdownOpen = false;
  isLeadBuyerDropdownOpen = false;
  isBuyerDropdownOpen = false;
  isDelegationDropdownOpen = false;
  isReportDropdownOpen = false;
  isAdminDropdownOpen = false;

  fullName: string = '';
  company: string = '';
  isAdmin: boolean = false;
  userEmail: string = '';
  constants = constantTxt;
  userData: UserData | null = null;
  canAccessBuyerMenu = false;
  canAccessLeadBuyerMenu = false;
  canAccessAdminMenu = false;
  selectedReportMenu: string = '';
  getUserDetailsSubscription!: Subscription;

  constructor(
    private readonly browserCheck: BrowserCheckService,
    private readonly router: Router,
    private readonly adminManageService: AdminManageService,
    private readonly cal: CalService,
    private readonly permissionCache: PermissionCacheService,
    private readonly sessionStorageService: SessionStorageService
  ) {}

  ngOnInit(): void {
    this.cal.isLoggedInSubject.subscribe((value) => {
      if (value) {
        this.fetchUserData();
      }
    });
    this.selectedReportMenu = this.router.url;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.selectedReportMenu = event.url;
      });
  }

  async fetchUserData() {
    this.fullName = this.browserCheck.getSessionStorage('UserName') || '';
    this.userEmail = this.browserCheck.getSessionStorage('UserEmail') || '';

    if (this.userEmail) {
      await this.getUserPermissions();
    }
  }

  async getUserPermissions() {
    if (!this.userEmail) {
      this.resetPermissions();
      return;
    }

    this.getUserDetailsSubscription = this.adminManageService
      .getUserDetails(this.userEmail)
      .subscribe({
        next: (res) => {
          const data = res.body;
          if (data) {
            this.userData = data;
            this.sessionStorageService.setItem(
              'loggedInUser',
              JSON.stringify(this.userData)
            );
            this.permissionCache.cachePermissions(
              data.userTypeSysAdmin || '',
              data.userTypeLead || '',
              data.userTypeBuyer || ''
            );
          }

          this.setUserPermissions();
        },
        error: () => {
          this.permissionCache.clearCache();
          this.resetPermissions();
        },
      });
  }

  private setUserPermissions() {
    if (!this.userData) {
      this.resetPermissions();
      return;
    }

    const isSysAdmin = this.userData.userTypeSysAdmin === '1';
    const isLead = this.userData.userTypeLead === '1';
    const isBuyer = this.userData.userTypeBuyer === '1';

    this.resetPermissions();

    if (isSysAdmin) {
      this.canAccessAdminMenu = true;
      this.isAdmin = true;
    }
    if (isLead) {
      this.canAccessLeadBuyerMenu = true;
    }
    if (isBuyer) {
      this.canAccessBuyerMenu = true;
    }

    this.checkCurrentRouteAccess();
  }

  private checkCurrentRouteAccess() {
    const currentUrl = this.router.url;

    const adminRoutes = [
      '/admin/manage-user',
      '/admin/manage-request',
      '/admin/company-code',
      '/admin/purchasing-code',
    ];

    const buyerRoutes = ['/buyer/request', '/buyer/other-request'];
    const leadBuyerRoutes = [
      '/lead-buyer/request',
      '/lead-buyer/other-request',
    ];

    // Check if current route is an admin route
    if (adminRoutes.some((route) => currentUrl.startsWith(route))) {
      if (!this.canAccessAdminMenu) {
        this.router.navigate(['/access-deny-user']);
        return;
      }
    }

    // Check if current route is a buyer route
    if (
      !this.canAccessBuyerMenu &&
      buyerRoutes.some((route) => currentUrl.startsWith(route))
    ) {
      this.router.navigate(['/access-deny-user']);
      return;
    }

    // Check if current route is a lead buyer route
    if (
      !this.canAccessLeadBuyerMenu &&
      leadBuyerRoutes.some((route) => currentUrl.startsWith(route))
    ) {
      this.router.navigate(['/access-deny-user']);
      return;
    }
  }

  private resetPermissions() {
    this.canAccessBuyerMenu = false;
    this.canAccessLeadBuyerMenu = false;
    this.canAccessAdminMenu = false;
    this.isAdmin = false;
  }

  onUserMenuClick(targetRoute: string) {
    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  onAdminMenuClick(targetRoute: string, event: Event) {
    if (!this.canAccessAdminMenu) {
      event.preventDefault();
      this.router.navigate(['/access-deny-user']);
      return;
    }

    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  onBuyerMenuClick(targetRoute: string, event: Event) {
    if (!this.canAccessBuyerMenu) {
      event.preventDefault();
      this.router.navigate(['/access-deny-user']);
      return;
    }

    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  onLeadBuyerMenuClick(targetRoute: string, event: Event) {
    if (!this.canAccessLeadBuyerMenu) {
      event.preventDefault();
      this.router.navigate(['/access-deny-user']);
      return;
    }

    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  onReportMenuClick(targetRoute: string) {
    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  onDelegationMenuClick(targetRoute: string) {
    // Navigate to the target route
    this.router.navigate([targetRoute]);
  }

  toggleUserDropdown() {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
  }

  closeUserDropdown() {
    this.isUserDropdownOpen = false;
  }

  toggleLeadBuyerDropdown() {
    this.isLeadBuyerDropdownOpen = !this.isLeadBuyerDropdownOpen;
  }

  closeLeadBuyerDropdown() {
    this.isLeadBuyerDropdownOpen = false;
  }

  toggleBuyerDropdown() {
    this.isBuyerDropdownOpen = !this.isBuyerDropdownOpen;
  }

  closeBuyerDropdown() {
    this.isBuyerDropdownOpen = false;
  }

  toggleDelegationDropdown() {
    this.isDelegationDropdownOpen = !this.isDelegationDropdownOpen;
  }

  closeDelegationDropdown() {
    this.isDelegationDropdownOpen = false;
  }

  toggleReportDropdown() {
    this.isReportDropdownOpen = !this.isReportDropdownOpen;
  }

  closeReportDropdown() {
    this.isReportDropdownOpen = false;
  }

  toggleAdminDropdown() {
    this.isAdminDropdownOpen = !this.isAdminDropdownOpen;
  }

  closeAdminDropdown() {
    this.isAdminDropdownOpen = false;
  }

  get isUserRequestActive(): boolean {
    const routes = [
      '/user/request',
      '/user/add-request',
      '/user/add-item',
      '/user/view-request',
      '/user/edit-request',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isUserOtherRequestActive(): boolean {
    const routes = ['/user/other-request', '/user/view-other-request'];
    return routes.includes(this.selectedReportMenu);
  }

  get isLeadBuyerRequestActive(): boolean {
    const routes = [
      '/lead-buyer/request',
      '/lead-buyer/view-request',
      '/lead-buyer/edit-request',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isLeadBuyerOtherRequestActive(): boolean {
    const routes = [
      '/lead-buyer/other-request',
      '/lead-buyer/view-other-request',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isBuyerRequestActive(): boolean {
    const routes = [
      '/buyer/request',
      '/buyer/view-request',
      '/buyer/edit-request',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isBuyerOtherRequestActive(): boolean {
    const routes = ['/buyer/other-request', '/buyer/view-other-request'];
    return routes.includes(this.selectedReportMenu);
  }

  get isManageDelegationActive(): boolean {
    const routes = [
      '/delegation/delegation-list',
      '/delegation/delegation-add',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isDetailsReportActive(): boolean {
    const routes = ['/report/report-sample'];
    return routes.includes(this.selectedReportMenu);
  }

  get isCycleReportActive(): boolean {
    const routes = ['/report/report-cycle'];
    return routes.includes(this.selectedReportMenu);
  }

  get isDescReportActive(): boolean {
    const routes = ['/report/report-item-detail'];
    return routes.includes(this.selectedReportMenu);
  }

  get isAdminManageUserActive(): boolean {
    const routes = ['/admin/manage-user'];
    return routes.includes(this.selectedReportMenu);
  }

  get isAdminRequestActive(): boolean {
    const routes = [
      '/admin/manage-request',
      '/admin/edit-request',
      '/admin/view-item',
    ];
    return routes.includes(this.selectedReportMenu);
  }

  get isPuchasingCodeActive(): boolean {
    const routes = ['/admin/purchasing-code'];
    return routes.includes(this.selectedReportMenu);
  }

  get isCompanyCodeActive(): boolean {
    const routes = ['/admin/company-code'];
    return routes.includes(this.selectedReportMenu);
  }

  ngOnDestroy(): void {
    if (this.getUserDetailsSubscription) {
      this.getUserDetailsSubscription.unsubscribe();
    }
  }
}
